import { Capacitor, registerPlugin } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { appLocalStorage } from '../utils/storage';
import { notificationRequest } from './api';

export const isNativeIOS = () => Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
const deviceSettings = registerPlugin('WiwDeviceSettings');
// The APNs registration timer starts only after checking the OS permission.
// A lost Capacitor reply after suspension must not keep this earlier stage
// (and the settings action waiting on it) pending indefinitely.
const permissionReply = (plugin, method, timeoutMs = 15000) => new Promise((resolve, reject) => {
  const timer = setTimeout(() => reject(new Error('PUSH_REGISTRATION_FAILED')), timeoutMs);
  Promise.resolve().then(() => plugin[method]()).then(
    value => { clearTimeout(timer); resolve(value); },
    error => { clearTimeout(timer); reject(error); },
  );
});
export function createNativePush({ plugin = PushNotifications, native = isNativeIOS, request = notificationRequest,
  settings = deviceSettings, storage = appLocalStorage, onReceived = () => {}, onAction = () => {}, onState = () => {} } = {}) {
  let session = null, generation = 0, disposed = false, enabled = true, pending = null, permissionRequest = null;
  const allowed = prompt => {
    if (enabled) return true;
    if (prompt) throw new Error('PUSH_REGISTRATION_UNAVAILABLE');
    return false;
  };
  let state = { platform: native() ? 'ios' : 'web', permission: 'unknown', registration: 'unregistered' };
  const publish = patch => { if (!disposed) { state = { ...state, ...patch }; onState(state); } };
  const refreshStatus = async () => {
    if (!native()) return;
    const version = generation;
    try {
      const permission = await permissionReply(plugin, 'checkPermissions');
      if (version === generation) publish({ permission: permission.receive,
        ...(permission.receive !== 'granted' ? { registration: 'unregistered' } : {}) });
    } catch { if (version === generation) publish({ registration: 'error' }); }
  };
  let initialization = null;
  const installation = () => {
    let id = storage.getItem('push-installation-id');
    if (!id) { id = crypto.randomUUID(); storage.setItem('push-installation-id', id); }
    return id;
  };
  const finish = (error, expected = pending) => {
    if (!pending || pending !== expected) return;
    const current = pending; pending = null; clearTimeout(current.timer);
    if (error) { publish({ registration: error.message === 'PUSH_SESSION_CHANGED' ? 'unregistered' : 'error' }); current.reject(error); }
    else { publish({ registration: 'registered' }); current.resolve(); }
  };
  async function removeListeners(entry) {
    entry.active = false;
    await Promise.allSettled(entry.handles.splice(0).map(handle => Promise.resolve().then(() => handle.remove())));
  }
  function ensureListeners() {
    if (!native() || disposed) return Promise.resolve();
    if (initialization) return initialization.promise;
    const entry = { active: true, handles: [], promise: null };
    initialization = entry;
    const listen = async (event, handler) => {
      if (disposed || !entry.active) return;
      const handle = await plugin.addListener(event, value => {
        if (entry.active && !disposed) return handler(value);
      });
      if (disposed || !entry.active) await handle.remove(); else entry.handles.push(handle);
    };
    entry.promise = (async () => {
      await listen('registration', async ({ value }) => {
        const current = session, version = generation, attempt = pending;
        if (!current || disposed || !enabled) return;
        try {
          const bindingVersion = (Number(storage.getItem('push-binding-version')) || 0) + 1;
          storage.setItem('push-binding-version', String(bindingVersion));
          await request('/devices', { method: 'POST', token: current.token, body: {
            installation_id: installation(), binding_version: bindingVersion, platform: 'ios',
            environment: process.env.REACT_APP_APNS_ENVIRONMENT || 'production', token: value,
          } });
          if (version === generation) finish(undefined, attempt);
        } catch (error) { if (version === generation) finish(error, attempt); }
      });
      await listen('registrationError', () => finish(new Error('PUSH_REGISTRATION_FAILED')));
      await listen('pushNotificationReceived', () => onReceived());
      await listen('pushNotificationActionPerformed', event => {
        const data = event?.notification?.data;
        const id = Number(data?.notification_id), userId = Number(data?.recipient_id);
        if (Number.isSafeInteger(id) && id > 0 && Number.isSafeInteger(userId) && userId > 0) onAction({ id, userId });
      });
    })().catch(async error => {
      // A transient native bridge failure must not poison every subsequent tap.
      // Fence callbacks before removing partially installed native listeners.
      await removeListeners(entry);
      if (initialization === entry) initialization = null;
      publish({ registration: 'error' });
      throw error;
    });
    return entry.promise;
  }
  void ensureListeners().catch(() => {});

  async function register(prompt = false) {
    if (!native() || disposed) return;
    if (!allowed(prompt)) return;
    if (!session) { if (prompt) throw new Error('PUSH_SESSION_CHANGED'); return; }
    const version = generation;
    await ensureListeners();
    if (disposed || version !== generation) { if (prompt) throw new Error('PUSH_SESSION_CHANGED'); return; }
    if (!allowed(prompt)) return;
    let permission = await permissionReply(plugin, 'checkPermissions');
    if (disposed || version !== generation) { if (prompt) throw new Error('PUSH_SESSION_CHANGED'); return; }
    if (!allowed(prompt)) return;
    const previouslyDenied = permission.receive === 'denied';
    if (prompt && ['prompt', 'prompt-with-rationale'].includes(permission.receive)) {
      // Match Android's permission deadline. A lost native reply must release
      // the shared toggle promise; a later foreground check can observe the
      // actual OS choice without repeating this abandoned registration.
      if (!permissionRequest) permissionRequest = permissionReply(plugin, 'requestPermissions', 60000)
        .finally(() => { permissionRequest = null; });
      permission = await permissionRequest;
    }
    if (disposed || version !== generation) { if (prompt) throw new Error('PUSH_SESSION_CHANGED'); return; }
    if (!allowed(prompt)) return;
    publish({ permission: permission.receive });
    if (permission.receive !== 'granted') {
      publish({ registration: 'unregistered' });
      if (storage.getItem('push-installation-id')) await request('/devices', { method: 'DELETE', token: session.token,
        body: { installation_id: installation() } }).catch(() => {});
      if (prompt) throw new Error(previouslyDenied ? 'PUSH_SETTINGS_REQUIRED' : 'PUSH_PERMISSION_DENIED');
      return;
    }
    // JS timers may have been suspended in the background. An expired attempt
    // must not swallow the first explicit retry/foreground reconciliation.
    if (pending && pending.expiresAt <= Date.now()) finish(new Error('PUSH_REGISTRATION_FAILED'), pending);
    if (pending) return pending.promise;
    publish({ registration: 'registering' });
    let resolve, reject;
    const promise = new Promise((yes, no) => { resolve = yes; reject = no; });
    const attempt = { promise, resolve, reject, timer: null, expiresAt: Date.now() + 15000 }; pending = attempt;
    attempt.timer = setTimeout(() => finish(new Error('PUSH_REGISTRATION_FAILED'), attempt), 15000);
    plugin.register().catch(() => finish(new Error('PUSH_REGISTRATION_FAILED'), attempt));
    return promise;
  }
  return {
    setEnabled(value) { enabled = value === true; },
    async setSession(next) {
      if (next?.token === session?.token && next?.userId === session?.userId) {
        if (next) await register(false);
        return;
      }
      const previous = session;
      const version = ++generation; finish(new Error('PUSH_SESSION_CHANGED')); session = next;
      publish({ registration: 'unregistered' });
      if (native() && previous && previous.userId !== next?.userId) {
        await plugin.removeAllDeliveredNotifications().catch(() => {});
      }
      if (next && !disposed && version === generation) await register(false);
    },
    register,
    refreshStatus,
    async openSettings() {
      if (!native() || disposed || !session) throw new Error('PUSH_SESSION_CHANGED');
      await settings.openNotifications();
    },
    async dispose() {
      disposed = true; generation++; session = null; finish(new Error('PUSH_SESSION_CHANGED'));
      const entry = initialization;
      if (entry) {
        entry.active = false;
        await entry.promise.catch(() => {});
        await removeListeners(entry);
      }
    },
  };
}
